/**
 * Key and Value of the tag can include alphanumerics, underscores, minuses, colons, periods, slashes, no spaces
 *
 * Follow the link for more details: https://docs.datadoghq.com/getting_started/tagging/#define-tags
 */
type Tag = `${string}:${string}`;

/**
 * Custom Metric to send to datadog
 *
 * @param {string} name: It is the name of the metrics. That will show in datadog.
 * @param {number} value: The numeric value of the metric. Default value is 1.
 * @param tag: Used to filter the metrics.
 *
 */

export interface Metric {
  name: `mmjs.${string}` | `ably.${string}`;
  value?: number;
  tags?: Tag[];
}

interface DatadogMetric {
  metric: string;
  type: number;
  points: [
    {
      timestamp: number;
      value: number;
    },
  ];
  tags: string[] | undefined;
}

interface DatadogDistribution {
  metric: string;
  points: [[number, number[]]];
  tags: string[] | undefined;
}

// Metric

export function createDatadogMetricObject(metric: Metric): DatadogMetric {
  metric.value ??= 1;

  return {
    metric: metric.name,
    type: 1,
    points: [
      {
        timestamp: Math.round(new Date().getTime() / 1000),
        value: metric.value,
      },
    ],
    tags: metric.tags,
  };
}

export function sendMetric(metric: Metric) {
  sendMetrics([metric]);
}

export function sendMetrics(metrics: Metric[]) {
  const datadogCustomMetrics = metrics.map(createDatadogMetricObject);
  const datadogCustomMetricSeries = {
    series: datadogCustomMetrics,
  };

  sendMetricSeries(datadogCustomMetricSeries);
}

interface DatadogMetricSeries {
  series: DatadogMetric[];
}

function sendMetricSeries(metricSeries: DatadogMetricSeries) {
  navigator?.sendBeacon?.('/client-metrics', JSON.stringify(metricSeries));
}

// Distribution

export function createDatadogDistributionObject(
  metric: Metric,
): DatadogDistribution {
  metric.value ??= 1;

  return {
    metric: metric.name,
    points: [[Math.round(new Date().getTime() / 1000), [metric.value]]],
    tags: metric.tags,
  };
}

export function sendDistribution(metric: Metric) {
  sendDistributions([metric]);
}

export function sendDistributions(metrics: Metric[]) {
  const datadogCustomDistributions = metrics.map(
    createDatadogDistributionObject,
  );
  const datadogCustomDistributionSeries = {
    series: datadogCustomDistributions,
  };

  sendDistributionSeries(datadogCustomDistributionSeries);
}

interface DatadogDistributionSeries {
  series: DatadogDistribution[];
}

function sendDistributionSeries(distributionSeries: DatadogDistributionSeries) {
  navigator?.sendBeacon?.(
    '/client-distributions',
    JSON.stringify(distributionSeries),
  );
}
